import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useRef,
  useState,
  FC,
  ReactNode,
} from 'react';
import { BaseChip } from './BaseChip';
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';
import { MenuItem, TextField } from '@mui/material';
import { BaseChipMenu } from './components/BaseChipMenu';
import { useFormCustomContext } from '../index';

type Props = ControllerRenderProps<FieldValues, string> & {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  tooltipTitle?: string | ReactNode;
  externalOnChange?: ({
    data,
    setValue,
  }: {
    data?: string;
    setValue?: UseFormSetValue<FieldValues>;
  }) => void;
};

export const TextInputChip: FC<Props> = ({
  value,
  label,
  name,
  placeholder,
  disabled,
  tooltipTitle,
  externalOnChange,
}) => {
  const [selectionHappened, setSelectionHappened] = useState(false);
  const { onChangeSubmit, formIsSubmittedOnFieldChange, setIsSubmitting } =
    useFormCustomContext();
  const { setValue } = useFormContext();
  const anchorEl = useRef<HTMLElement | null>(null);
  const [isOpen, setOpen] = useState(false);

  const handleToggleChip = useCallback((e?: MouseEvent<HTMLElement>) => {
    setOpen((isOpen) => !isOpen);

    if (e) {
      anchorEl.current = e.currentTarget;
    }
  }, []);

  const handleCloseMenu = useCallback(
    (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      handleToggleChip();

      if (formIsSubmittedOnFieldChange && selectionHappened) {
        setIsSubmitting(true);
        setTimeout(onChangeSubmit, 0);
      }
      setSelectionHappened(false);
    },
    [
      formIsSubmittedOnFieldChange,
      handleToggleChip,
      onChangeSubmit,
      selectionHappened,
      setIsSubmitting,
    ],
  );

  const handleChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectionHappened(true);
      const value = event.target.value;
      const pattern = /^[A-Za-z0-9]*$/;

      if (pattern.test(value) || value === '') {
        setValue(name, value ?? '');
        externalOnChange?.({ data: value ?? '', setValue });
      }
    },
    [setValue, name, externalOnChange],
  );

  const handleResetValues = useCallback(async () => {
    setValue(name, '');
    externalOnChange?.({ data: '', setValue });

    if (formIsSubmittedOnFieldChange) {
      setIsSubmitting(true);
      await onChangeSubmit();
    }
  }, [
    name,
    formIsSubmittedOnFieldChange,
    onChangeSubmit,
    setValue,
    externalOnChange,
    setIsSubmitting,
  ]);

  return (
    <BaseChip
      formattedSelectedValue={value}
      label={label}
      name={name}
      isOpen={isOpen}
      disabled={disabled}
      tooltipTitle={tooltipTitle}
      resetValue={handleResetValues}
      onClick={handleToggleChip}
    >
      <BaseChipMenu
        isOpen={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={handleCloseMenu}
      >
        <MenuItem
          sx={{ display: 'flex', alignItems: 'flex-end' }}
          disableRipple
        >
          <TextField
            size="small"
            onChange={handleChangeInput}
            variant="outlined"
            value={value}
            label={label}
            placeholder={placeholder}
            inputProps={{
              maxLength: 25,
            }}
            InputLabelProps={{ shrink: true }}
          />
        </MenuItem>
      </BaseChipMenu>
    </BaseChip>
  );
};
